import { FileUploader, Stack } from "@carbon/react";
import useAxios from "axios-hooks";
import React from "react";
import { Controller, useForm } from "react-hook-form";

import { ConfirmActionModal } from "src/components/Modal";
import { useToast } from "src/components/Toasts/ToastContainer.tsx";
import { mapToModalError, useMapToMutationError } from "src/errors";
import { ScenarioVersion } from "src/http/ScenarioVersion.api.ts";
import { useT } from "src/internationalization";

export const ImportVersionModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { showSuccessToast } = useToast();
  const mapToMutationError = useMapToMutationError();

  const [{ loading: postLoading, error: postError }, executeImport] = useAxios(
    {
      url: ScenarioVersion.ScenarioVersion().postScenarioVersion(),
      method: "POST",
      headers: { "Content-Type": "application/json" },
    },
    { manual: true }
  );
  const t = useT();
  const { handleSubmit, control } = useForm<{ scenarioVersion: string }>();

  const onSubmit = async (data) => {
    if (data.scenarioVersion) {
      const reader = new FileReader();
      reader.readAsText(data.scenarioVersion);
      const jsonContent = await new Promise((resolve) => {
        reader.onload = () => {
          resolve(JSON.parse(reader.result as string));
        };
      });
      await executeImport({ data: jsonContent });
      onClose();
      showSuccessToast(t("import.scenario.version.modal.success.message"));
    }
  };

  return (
    <>
      <ConfirmActionModal
        size={"md"}
        onClose={onClose}
        onConfirm={handleSubmit(onSubmit)}
        error={mapToModalError(mapToMutationError(postError))}
        loading={postLoading}
        title={t("import.scenario.version.modal.title")}
        subtitle={t("import.scenario.version.modal.subtitle")}
        confirmButtonText={t("general.actions.confirm")}
        initialFocus="#importScenarioFile"
      >
        <Stack gap={5}>
          <div>
            <span>
              {t("import.scenario.version.modal.form.description.file", { extensions: ".json" })}
            </span>
          </div>
          <div className="flex">
            <Controller
              defaultValue={""}
              render={({ field, fieldState }) => (
                <Stack gap={2}>
                  <FileUploader
                    buttonLabel={t("import.scenario.version.modal.form.button.file")}
                    name="file"
                    size="lg"
                    id="importScenarioFile"
                    accept={["application/json"]}
                    multiple={false}
                    filenameStatus={"edit"}
                    onChange={(_, data) => {
                      const file = data?.currentFiles?.[0]?.file ?? null;
                      field.onChange(file);
                    }}
                  />
                  {fieldState.invalid && (
                    <div className="text-textError">
                      {t("import.scenario.version.modal.form.content.required")}
                    </div>
                  )}
                </Stack>
              )}
              name={"scenarioVersion"}
              control={control}
              rules={{ required: true }}
            />
          </div>
        </Stack>
      </ConfirmActionModal>
    </>
  );
};
